/**
 * ChallengeInfo Component
 * Main view for displaying detailed information about a challenge
 *
 * Features:
 * - Shows organizer logo/name
 * - Displays challenge actions (join/leave/etc.)
 * - Lists challenge details: title, period, goals, prizes, description
 * - Supports both legacy and new goals configuration structures
 */

import React, { useEffect, useCallback } from 'react'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import YumGrid from '../../components/YumGrid/YumGrid'
import YumChallengeActions from '../../components/YumChallengeActions/YumChallengeActionsWrapper'
import { useChallengeInfoStyles } from './ChallengeInfo.styles'
import { IChallengeInfoProps } from './ChallengeInfo.types'
import { OrganizerLogo, ChallengeInfoData } from './components'
import YumGoalRankingExportButtonWrapper from '../../components/YumGoalRanking/YumGoalRankingHeader/YumGoalRankingExportButtonWrapper'
import { Role } from '../../services/api.types'

// Re-export types and helpers for backwards compatibility
export { dailyMaxLabel, totalMaxLabel, getPeriodWithDays } from './ChallengeInfo.helpers'
export type { IChallengeInfoProps } from './ChallengeInfo.types'

// Re-export components for backwards compatibility
// These were previously exported from this file
export { default as PointsFormula } from './components/PointsFormula'
export { default as TotalPointsChallenges } from './components/TotalPointsChallenges'

// Re-export styles for backwards compatibility
export { useChallengeInfoStyles as useStyles } from './ChallengeInfo.styles'

/**
 * ChallengeInfo displays detailed information about a specific challenge
 *
 * This component:
 * 1. Fetches challenge data on mount (if not already loaded)
 * 2. Displays organizer branding (logo or company name)
 * 3. Shows challenge actions (join/leave buttons)
 * 4. Lists all challenge details in an organized format
 */
const ChallengeInfo: React.FC<IChallengeInfoProps> = ({
  goalId,
  goal,
  goals,
  divisionCategories,
  getSingleChallengeWithProgress,
  cancelGetChallengesWithProgress,
  hasRoleInCompany,
}) => {
  // Initialize styles hook BEFORE any conditional returns
  // This follows React's Rules of Hooks
  const classes = useChallengeInfoStyles()

  /**
   * Effect: Load challenge data when component mounts or goalId changes
   * Also cancels any ongoing requests to prevent race conditions
   */
  useEffect(() => {
    if (goalId) {
      // Cancel any ongoing getChallengesWithProgress request when navigating to this view
      cancelGetChallengesWithProgress()
      // Then load the single challenge
      getSingleChallengeWithProgress({ goalId })
    }
  }, [goalId, getSingleChallengeWithProgress, cancelGetChallengesWithProgress])

  // Early return if goal not loaded yet
  // Note: Hooks are called above this point to satisfy Rules of Hooks
  if (!goal) {
    return null
  }
  const isAdmin = hasRoleInCompany(goal.companyId, Role.ADMIN)

  return (
    <div className={classes.wrapper}>
      {/* Notification container for displaying alerts */}
      <NotificationsWrapper />

      {/* Header section: Organizer logo and challenge actions */}
      <YumGrid className={classes.grid}>
        <OrganizerLogo goal={goal} />
        <YumGrid>
          {isAdmin && <YumGoalRankingExportButtonWrapper goalId={goalId} />}
          <YumChallengeActions goal={goal} />
        </YumGrid>
      </YumGrid>

      {/* Challenge details list */}
      <ChallengeInfoData goal={goal} goals={goals} divisionCategories={divisionCategories} />
    </div>
  )
}

export default ChallengeInfo
